import { Button, EmptyState, ErrorState, ObjectRecordPage, PageContent, Spinner } from '@olytic/foundation';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { authenticatedFetch } from '../auth/authClient';
import { objects } from '../config/objects';
import { useDocumentTitle } from '../documentTitle';
import type { ObjectKey } from '../navigation';
import { loadLookupOptions } from './lookupOptions';
import { metadataFor } from './ObjectRecordView';
import { ObjectsApiError, objectMetaUrl, type ObjectRow, parseObjectMetaResponse, parseRecordResponse, recordUrl } from './objectsApi';
import { listPath, recordPath } from './routes';
import { cloneBody, cloneSource, createRecord, creatable, NOT_WRITABLE, withEditors, type Writable } from './writes';

type Load =
  | { state: 'loading' }
  | { state: 'error'; message: string; missing?: boolean }
  | { state: 'ready'; start: ObjectRow; writable: Writable };

const messageOf = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message.length > 0 ? error.message : fallback;

/**
 * A new record at /<object>/new?from=<id>: the record page in edit mode, every
 * field starting from <id>'s values (unique ones blank). Nothing is written
 * until Save; Cancel goes back to <id>.
 */
export function ObjectCloneView({ object }: { object: ObjectKey }): JSX.Element {
  const [params] = useSearchParams();
  const from = params.get('from') ?? '';
  const navigate = useNavigate();
  const base = objects[object];
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useDocumentTitle(`New ${base.singular.toLowerCase()}`);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setLoad({ state: 'loading' });
    (async () => {
      const [meta, source] = await Promise.all([
        authenticatedFetch(objectMetaUrl(object), { signal }).then(parseObjectMetaResponse),
        from ? authenticatedFetch(recordUrl(object, from), { signal }).then(parseRecordResponse) : Promise.resolve({ id: '' } as ObjectRow),
      ]);
      if (signal.aborted) return;
      const writable = creatable(meta.writable ?? NOT_WRITABLE);
      setLoad({ state: 'ready', start: cloneSource(metadataFor(object, source), writable, source), writable });
    })().catch((error: unknown) => {
      if (signal.aborted) return;
      const missing = error instanceof ObjectsApiError && error.status === 404;
      setLoad({ state: 'error', missing, message: messageOf(error, `The ${base.singular.toLowerCase()} could not be loaded.`) });
    });
    return () => controller.abort();
  }, [object, from, attempt, base.singular]);

  const ready = load.state === 'ready' ? load : null;
  const metadata = useMemo(
    () => (ready ? withEditors(metadataFor(object, ready.start), ready.writable, loadLookupOptions) : null),
    [object, ready],
  );

  const back = () => navigate(from ? recordPath(object, from) : listPath(object));

  if (load.state === 'loading') {
    return (
      <PageContent as="section" width="wide">
        <Spinner label={`Loading ${base.singular.toLowerCase()}…`} />
      </PageContent>
    );
  }
  if (load.state === 'error') {
    return (
      <PageContent as="section" width="wide">
        {load.missing ? (
          <EmptyState
            icon={base.icon}
            title={`${base.singular} not found`}
            description="The record to copy may have been deleted, or the link is wrong."
            action={
              <Button asChild variant="neutral">
                <Link to={listPath(object)}>All {base.plural.toLowerCase()}</Link>
              </Button>
            }
          />
        ) : (
          <ErrorState title={`Could not start a new ${base.singular.toLowerCase()}`} description={load.message} onRetry={() => setAttempt((n) => n + 1)} />
        )}
      </PageContent>
    );
  }
  if (!load.writable.create || !metadata) {
    return (
      <PageContent as="section" width="wide">
        <EmptyState
          icon={base.icon}
          title={`${base.plural} can't be created here`}
          action={
            <Button variant="neutral" onClick={back}>
              Back
            </Button>
          }
        />
      </PageContent>
    );
  }

  const { start, writable } = load;
  return (
    <PageContent as="section" width="wide">
      <ObjectRecordPage<ObjectRow>
        metadata={metadata}
        record={start}
        create={{ onCancel: back }}
        onFieldsSave={async (values) => {
          const id = await createRecord(authenticatedFetch, object, cloneBody(metadata, writable, start, values));
          navigate(recordPath(object, id), { replace: true });
        }}
      />
    </PageContent>
  );
}
